import { ChangeDetectorRef, Component } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import _ from 'lodash';
import { tap } from 'rxjs';
import { SchoolDetails } from 'src/app/modules/placement/models/placement-finder/school-list.model';
import { PlacementService } from 'src/app/modules/placement/services/placement.services';
import { LoadingService } from 'src/app/services/loading.service';
import { ReferenceService } from 'src/app/services/reference.service';
import {
  PLACEMENT_FILTER_CONSTANTS,
  PlacementFilterConstants,
} from '../../constants/placement-filters.constants';
import { ActionType } from '../../enums/action-type';
import { IReferenceData } from '../../interfaces/form/reference-data.model';
import { PlacementFiltersReferenceData } from '../../interfaces/placement-filters-refernce';
import { StudentProfile } from '../../interfaces/student';
import { ISedmsFormOutput } from '../../interfaces/form/sedms-form-output.model';
import { LiveRegionService } from 'src/app/shared/services/live-region.service';

@Component({
  selector: 'app-schoolage-placement-filters',
  templateUrl: './schoolage-placement-filters.component.html',
  styleUrl: './schoolage-placement-filters.component.scss',
  standalone: false,
})
export class SchoolagePlacementFiltersComponent extends DialogContentBase {
  form: FormGroup;
  constants: PlacementFilterConstants = PLACEMENT_FILTER_CONSTANTS;
  studentDetails: StudentProfile;
  iepFormSk: string;
  referenceData: PlacementFiltersReferenceData;
  defaultFilters: SchoolDetails;
  previousFilters: SchoolDetails;
  isSummer: boolean = false;
  schoolYears: IReferenceData[] = [];
  currentAction: 'select' | 'deselect' | null = null;
  programRatioList: IReferenceData[];
  schoolsList: IReferenceData[] = [];
  currentFilters: SchoolDetails;
  readonly groupSiteTypes: string = 'siteTypes';
  programList: IReferenceData[];
  isReset: boolean = false;

  constructor(
    private fb: FormBuilder,
    dialog: DialogRef,
    private cdRef: ChangeDetectorRef,
    library: FaIconLibrary,
    private referenceService: ReferenceService,
    private loadingService: LoadingService,
    private placementService: PlacementService<ISedmsFormOutput>,
    private route: ActivatedRoute,
    private liveRegionService: LiveRegionService,
  ) {
    super(dialog);

    this.loadingService.show();
    this.referenceService
      .getDropDownValues('SERatio')
      .pipe(
        tap((data: IReferenceData[]) => {
          if (data) {
            this.programRatioList = [...data];

            const allPrograms: IReferenceData[] =
              PLACEMENT_FILTER_CONSTANTS.programList;
            if (!this.studentDetails?.isPreSchool) {
              this.programList = allPrograms.filter(
                (p: IReferenceData) =>
                  p.value === 'ICT' || p.value === 'SC' || p.value === 'SETSS',
              );
            }
          }
        }),
      )
      .subscribe(() => {
        this.initializeForm(
          this.isReset
            ? {}
            : this.previousFilters
              ? this.previousFilters
              : this.defaultFilters,
        );
        this.isReset = false;

        this.loadingService.hide();
        this.currentFilters = this.previousFilters;
      });

    library.addIcons(faXmark);
  }

  initializeForm(filters: SchoolDetails | null | Record<string, never>): void {
    if ((filters as any)?.filters) {
      this.initializeFormValues((filters as any)?.filters);
    } else {
      this.initializeFormValues(filters as SchoolDetails);
    }
  }

  initializeFormValues(filters: SchoolDetails | null | Record<string, never>): void {
    const selectedDistrictValues: string[] = Array.isArray(filters?.district)
      ? filters.district
      : [filters?.district].filter(Boolean) as string[];

    const selectedProgramCodes: string[] = Array.isArray(filters?.scscis)
      ? filters.scscis
      : [filters?.scscis].filter(Boolean);
    
    const selectedRatio = Array.isArray(filters?.scSpecialRatio)
      ? filters?.scSpecialRatio
      : [filters?.scSpecialRatio]?.filter(Boolean);

    this.form = this.fb.group({
      isShowClassesSupportStudentGrade: [
        filters?.isShowClassesSupportStudentGrade ?? false,
      ],
      isAccessibleSchoolRequired: [
        filters?.isAccessibleSchoolRequired ?? false,
      ],
      restrictStudentsDistrict: [filters?.restrictStudentsDistrict ?? false],
      programs: [
        this.programList.filter((prog: IReferenceData) =>
          selectedProgramCodes.includes(prog.value as string),
        ),
      ],
      ratio: [
        this.referenceData?.seRatio?.filter((r: IReferenceData) =>
          selectedRatio.includes(r.description),
        ),
      ],
      placementRecType: [
        this.referenceData?.placementRecType?.find(
          (p: IReferenceData) =>
            p.description === filters?.placementRecType ||
            p.value === filters?.placementRecType,
        ) ?? null,
      ],
      district: [
        this.referenceData?.district?.filter((district: IReferenceData) =>
          selectedDistrictValues.includes(district.value as string),
        ),
      ],
      borough: [
        this.referenceData?.borough?.find(
          (b: IReferenceData) =>
            b.description === filters?.borough || b.value === filters?.borough,
        ) ?? null,
      ],
      schoolYearTerm: [
        this.schoolYears?.find(
          (y: IReferenceData) =>
            y.value === filters?.schoolYear ||
            y.description === filters?.schoolYear,
        ) ?? null,
      ],
    });
  }

  public close(searchObj: { schoolsList?: IReferenceData[]; filters?: SchoolDetails } | null): void {
    this.dialog.close(searchObj);
  }

  toggleSelectAll(select: boolean): void {
    if (select && this.currentAction !== ActionType.Select) {
      this.onCheckboxChange(true, this.groupSiteTypes);
      this.currentAction = ActionType.Select;
    } else if (!select && this.currentAction !== ActionType.Deselect) {
      this.onCheckboxChange(false, this.groupSiteTypes);
      this.currentAction = ActionType.Deselect;
    }
  }

  onCheckboxChange(event: boolean, type?: string): void {
    const CONTROLS: string[] = _.map(this.constants[type], 'control');
    CONTROLS.forEach((ctrl: string) => {
      this.form.get(ctrl).patchValue(event);
    });
  }

  onShowresults(): void {
    this.close({ schoolsList: this.schoolsList, filters: this.getSchoolsList });
  }

  get getSchoolsList(): SchoolDetails {
    const val: any = this.form.value;

    const searchObj: SchoolDetails = {
      scscis: val.programs?.length
        ? val.programs.map((p: IReferenceData) => p.value as string)
        : [],
      scSpecialRatio: this.form?.value?.ratio?.length
        ? this.form.value.ratio.map((r: any) => r.value)
        : [],
      isAccessibleSchoolRequired: val.isAccessibleSchoolRequired,
      restrictStudentsDistrict: val.restrictStudentsDistrict,
      isShowClassesSupportStudentGrade: val?.isShowClassesSupportStudentGrade,
      showByAvailableSeats: this.form.value.showClassesWith === 0,
      homeDistrict: Number(this.studentDetails?.homeDistrict),
      // placementRecType: val.placementRecType?.description ?? null,
      district: val.district?.length
        ? val.district.map((d: IReferenceData) => d.value as string)
        : [],

      borough: val.borough ? (val.borough.value as string) : null,
      isSummer: this.isSummer,
      nycId: this.route.snapshot.params.nycId,
      schoolYear: this.form?.value?.schoolYearTerm?.value,
    } as SchoolDetails;
    this.loadingService.show();
    this.currentFilters = searchObj;
    return searchObj;
  }

  resetFilters(): void {
    this.isReset = false;
    this.previousFilters = null;
    this.initializeForm(this.defaultFilters);
    this.currentFilters = this.defaultFilters;
    this.liveRegionService.announceFiltersCleared();
  }
}
